/**
 * WHICH OF THE CABINET'S SIDEBAR ENTRIES CARRY A NUMBER, AND WHICH OPERATION SERVES IT - the
 * "attention badges" of the `NavRail` row (`docs/ui/03-components.md`). The prototype draws two,
 * Articles and Comments (`CAB_NAV`: `attn:2`, `attn:5`), the same two facts its Overview words as
 * "2 articles need changes" and "5 comments are waiting for a reply". BOTH ARE SERVED, SO BOTH ARE
 * DRAWN - the comments' on the Inbox (design 24, T17), the messages' too (B2-INBOX), below.
 *
 * ARTICLES. `listOwnArticles` filters by status and its page carries `total`, "how many rows the
 * filter matches in total, not just on this page" (authors-api.yaml, :4267-4295). The badge
 * asks for ONE row of `status=changes_requested` and reads the server's own count: the articles
 * waiting on this author's changes - and, since videos (v1.0.29) are listed one kind at a time, the
 * same of the videos, the two counts summed (`countPiecesNeedingChanges`).
 *
 * COMMENTS. `listCommentsOnMyArticles` has the filter, `unansweredOnly` - "root comments this
 * author has not replied to" (authors-api.yaml, :2301) - and since decision-log row 213
 * its page, `CabinetCommentPage`, carries a `total` EXACTLY when that filter is asked: how many such
 * roots there are across every page (authors-api.yaml). The unfiltered list, an
 * append-heavy stream, carries none. The badge asks for ONE unanswered root and reads that `total`.
 *
 * NOTHING IS COUNTED IN THE BROWSER, AND AN ABSENT `total` IS "NOT SERVED". A page that comes back
 * without one - a server from before row 213, or a read that lost the filter on its way - is an
 * answer that carried no count, and `useNavAttention` (`@moonx/ui`) draws nothing for it: no badge
 * and no number in the entry's name. Never the length of the one row asked for, which would say "1"
 * whatever the truth, and never a zero, which would be a number the platform did not say.
 *
 * THE OVERVIEW ASKS THE SAME QUESTIONS THROUGH THE SAME FUNCTIONS - `countArticlesNeedingChanges`
 * and `countCommentsAwaitingReply` below, read by `routes/dashboard/NeedsAttention.tsx` - so its
 * "Needs your attention" rows and these badges can never be asking different things. EACH SURFACE
 * STILL SENDS ITS OWN REQUEST, so on the Overview both counts are asked twice, once by the sidebar
 * and once by the screen. That is chosen over sharing one answer: this app has no query cache
 * (`features/cabinet/state/use-async.ts` says why), and a shared in-flight read would carry one
 * surface's abort into the other - the sidebar cancels its reads whenever it moves on, and the
 * Overview's row would go with them. Asked in the same moment, the two answers differ only if a
 * verdict or a comment lands in between.
 *
 * ★ THE CREATOR TERMS ARE NOT A COUNT, AND THEY ARE NOT READ HERE (design 14, F11). The cabinet
 * walk of 2026-10-01 found an author whose current terms were not accepted - the Support page said
 * "You need to accept the current terms" - while the Overview's "Needs your attention" listed only
 * drafts. The fact is the session's `termsAcceptanceRequired`, "True when the live terms version
 * has not been accepted. It blocks submitting, resubmitting and publishing — and never a payout"
 * (the contract's `AuthorSession`), which the app ALREADY HOLDS: `GET /v1/me` is the one read that
 * renders the app, `AuthSessionProvider` keeps it, and re-reads it the moment an acceptance lands
 * on `/terms` (`routes/support/session-routes.tsx`, `LiveTermsInterstitial`). The gate's detour,
 * the Support page's block and the editor's refusal notice all answer from that one fact, so this
 * file sends no second request for it - not `GET /v1/me` again, and not `GET /v1/terms/current`,
 * whose own reader is internal to the support feature (`features/support/index.ts`). What lives
 * here is the rule (`termsAcceptanceOwed`) and the address the row links to.
 */
import type { NavAttentionSource } from '@moonx/ui';
import { i18n } from '../../i18n';
import { INBOX_PATH } from '../../routes/inbox/paths';
import { CABINET_HOME_PATH, termsPathFor } from '../auth/navigation';
import type { AuthorSession, CabinetApi } from '../cabinet/api';

/**
 * The Articles screen's address (`features/cabinet/routes.ts`), where the count is shown.
 * `test/features/shell/cabinet-rail.spec.tsx` fails if the sidebar stops listing it.
 */
export const ARTICLES_PATH = '/articles';

/**
 * Where the unanswered comments are listed and answered: since design 24 (T17) the Inbox on its
 * Comments tab (`features/cabinet/routes.ts`), which the Overview's row links to.
 */
export const COMMENTS_PATH = '/comments';

/**
 * The articles waiting on this author's changes: `listOwnArticles` for ONE row of
 * `changes_requested`, and the page's own `total`. `undefined` when the page carries none.
 */
export async function countArticlesNeedingChanges(
  api: CabinetApi,
  signal: AbortSignal,
): Promise<number | undefined> {
  return (await api.listOwnArticles({ status: ['changes_requested'], limit: 1 }, signal)).total;
}

/**
 * The videos sent back for changes (v1.0.29). `listOwnArticles` lists one kind at a time and
 * articles when none is named, so the videos are their own question, asked the same way.
 */
export async function countVideosNeedingChanges(
  api: CabinetApi,
  signal: AbortSignal,
): Promise<number | undefined> {
  return (
    await api.listOwnArticles({ kind: 'video', status: ['changes_requested'], limit: 1 }, signal)
  ).total;
}

/**
 * The sidebar's one count on Articles, which lists both kinds: the articles' and the videos', each
 * the server's own. Either unserved makes the sum unserved, as the Inbox's parts do.
 */
export async function countPiecesNeedingChanges(
  api: CabinetApi,
  signal: AbortSignal,
): Promise<number | undefined> {
  const [articles, videos] = await Promise.all([
    countArticlesNeedingChanges(api, signal),
    countVideosNeedingChanges(api, signal),
  ]);
  return articles === undefined || videos === undefined ? undefined : articles + videos;
}

/**
 * The root comments this author has not answered, on every article of theirs:
 * `listCommentsOnMyArticles` for ONE row with `unansweredOnly`, and the page's own `total`.
 * `undefined` when the page carries none - never the length of the page.
 */
export async function countCommentsAwaitingReply(
  api: CabinetApi,
  signal: AbortSignal,
): Promise<number | undefined> {
  return (await api.listCommentsOnMyArticles({ unansweredOnly: true, limit: 1 }, signal)).total;
}

/**
 * The readers' messages waiting for this author's reply (B2-INBOX): `listSupporterMessages` for ONE
 * row with `unansweredOnly` - no reply yet, not taken back by its sender, hidden by nobody, sent in
 * the last 7 days, as the comments' filter is bounded - and the page's own `total`, which this list
 * carries on every page and which then counts what waits. `undefined` when the page carries none.
 */
export async function countMessagesAwaitingReply(
  api: CabinetApi,
  signal: AbortSignal,
): Promise<number | undefined> {
  return (await api.listSupporterMessages({ unansweredOnly: true, limit: 1 }, signal)).total;
}

/**
 * ★ WHAT WAITS IN THE INBOX FOR A REPLY - the sidebar's one count for it (design 24, T17): the sum
 * of what each of its tabs has the server count - the comments' and, since batch 2 (B2-INBOX), the
 * messages'. The "MoonX team" tab has nothing to count until its chat exists. A part whose answer
 * carries no count, or fails, makes the whole sum unserved, never a smaller number.
 */
const INBOX_PARTS: ReadonlyArray<
  (api: CabinetApi, signal: AbortSignal) => Promise<number | undefined>
> = [countCommentsAwaitingReply, countMessagesAwaitingReply];

export async function countInboxAwaitingReply(
  api: CabinetApi,
  signal: AbortSignal,
): Promise<number | undefined> {
  const parts = await Promise.all(INBOX_PARTS.map((count) => count(api, signal)));
  let sum = 0;
  for (const part of parts) {
    if (part === undefined) {
      return undefined;
    }
    sum += part;
  }
  return sum;
}

/**
 * What each entry with a count says after its label - "Articles, 2 need changes", "Inbox, 5
 * awaiting your reply" - by address. The same for every client, so it is not built per read; the
 * words are the catalogue's, looked up when the sidebar draws, so they follow the language.
 */
export const CABINET_ATTENTION_WORDS: ReadonlyMap<string, (count: number) => string> = new Map([
  [ARTICLES_PATH, (count: number) => i18n.t('attention.articles', { ns: 'shell', count })],
  [INBOX_PATH, (count: number) => i18n.t('attention.inbox', { ns: 'shell', count })],
]);

/**
 * Whether an agreement waits on this author before they can submit an article for review: the
 * session says so, AND the author could otherwise send an article. The second half is the gate's
 * own (`termsInterstitialDue`, features/auth/session/session-state.ts): a disabled author has no
 * send for an acceptance to unblock - the Overview tells them their account is on hold instead -
 * and an applicant accepts inside the application, so a row saying "until then you cannot submit
 * an article for review" would tell either the wrong reason. `=== true` both times: each field is
 * optional on `AuthorSession`, and absent is not a demand.
 *
 * ★ THE SESSION'S ANSWER FIRST (design 25, section 3.4). `GET /v1/me` sends
 * `termsAcceptanceRequired` now - "approved, and owing a SUBMISSION document", the author agreement
 * or the editorial standards, never a payout document - and when it answers, it decides, and the
 * Overview asks nothing else (`LiveOverview`, routes/dashboard/DashboardRoute.tsx). It was silent
 * until then (found on the re-walk of design 14), so for a server that is silent the Support page's
 * rule stands (features/support/SupportPage.tsx, `reacceptanceOwed`): the author agreement's own
 * `getCurrentTerms` `accepted` - handed in as `termsAccepted`, `undefined` while unread - decides.
 * Never used to contradict a session that said no.
 */
export function termsAcceptanceOwed(
  session: AuthorSession | null | undefined,
  termsAccepted?: boolean,
): boolean {
  if (session?.canUseEditor !== true) {
    return false;
  }
  if (session.termsAcceptanceRequired !== undefined) {
    return session.termsAcceptanceRequired === true;
  }
  return termsAccepted === false;
}

/**
 * Where the terms are accepted - `/terms`, the same screen the gate's detour and the Support page's
 * button open - carrying the Overview as `next`, so "Continue" after the one click comes back here
 * (features/auth/navigation.ts, `termsPathFor`).
 */
export const TERMS_ATTENTION_HREF: string = termsPathFor(CABINET_HOME_PATH);

/** The counts, read through `api` - the one client the screens read through. */
export function cabinetAttention(api: CabinetApi): ReadonlyMap<string, NavAttentionSource> {
  return new Map([
    [ARTICLES_PATH, { read: (signal: AbortSignal) => countPiecesNeedingChanges(api, signal) }],
    [INBOX_PATH, { read: (signal: AbortSignal) => countInboxAwaitingReply(api, signal) }],
  ]);
}
